import { useRef, useState } from 'react';
import { Upload, X, FileText, Image, Music, Video, File, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useFileUpload } from '@/hooks/useFileUpload';

interface FileUploadFieldProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  accept?: string;
  fileType?: 'pdf' | 'image' | 'audio' | 'video' | 'document' | 'any';
  placeholder?: string;
  required?: boolean;
  showUrlInput?: boolean;
}

const fileTypeIcons: Record<string, React.ElementType> = {
  pdf: FileText,
  image: Image,
  audio: Music,
  video: Video,
  document: FileText,
  any: File,
};

const acceptMap: Record<string, string> = {
  pdf: '.pdf',
  image: '.png,.jpg,.jpeg,.gif,.webp,.svg',
  audio: '.mp3',
  video: '.mp4',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
  any: '.pdf,.png,.jpg,.jpeg,.gif,.webp,.svg,.mp3,.mp4,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip',
};

export function FileUploadField({
  label,
  value,
  onChange,
  accept,
  fileType = 'any',
  placeholder = 'https://example.com/file.pdf',
  required = false,
  showUrlInput = true,
}: FileUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading, uploadProgress } = useFileUpload();
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: string } | null>(null);
  const [mode, setMode] = useState<'upload' | 'url'>(value && !value.includes('/uploads/') ? 'url' : 'upload');
  const [isDragging, setIsDragging] = useState(false);

  const IconComponent = fileTypeIcons[fileType] || File;
  const acceptString = accept || acceptMap[fileType] || acceptMap.any;

  const processFile = async (file: File) => {
    const result = await uploadFile(file, fileType);
    if (result) {
      onChange(result.url);
      setUploadedFile({ name: result.originalName, size: result.sizeFormatted });
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleRemoveFile = () => {
    onChange('');
    setUploadedFile(null);
  };

  const isUploaded = uploadedFile && value.includes('/uploads/');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {showUrlInput && (
          <div className="flex gap-1 bg-muted rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setMode('upload')}
              className={cn(
                'text-xs px-2 py-1 rounded transition-colors',
                mode === 'upload' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Upload
            </button>
            <button
              type="button"
              onClick={() => setMode('url')}
              className={cn(
                'text-xs px-2 py-1 rounded transition-colors',
                mode === 'url' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              URL
            </button>
          </div>
        )}
      </div>

      {mode === 'url' ? (
        <Input
          type="url"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setUploadedFile(null);
          }}
          placeholder={placeholder}
        />
      ) : (
        <div className="space-y-2">
          {/* Upload area */}
          {!isUploaded && !isUploading && (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                isDragging
                  ? 'border-primary bg-primary/10'
                  : 'hover:border-primary/50 hover:bg-primary/5 border-muted-foreground/25'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptString}
                onChange={handleFileSelect}
                className="hidden"
              />
              <IconComponent className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium">Click to upload or drag & drop</p>
              <p className="text-xs text-muted-foreground mt-1">
                {fileType === 'pdf' ? 'PDF files up to 50MB' :
                 fileType === 'image' ? 'PNG, JPG, GIF, WebP, SVG up to 50MB' :
                 `Supported files up to 50MB`}
              </p>
            </div>
          )}

          {/* Upload progress */}
          {isUploading && (
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span className="text-sm font-medium">Uploading...</span>
                <span className="text-sm text-muted-foreground ml-auto">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {/* Uploaded file info */}
          {isUploaded && (
            <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                  <p className="text-xs text-muted-foreground">{uploadedFile.size}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  onClick={handleRemoveFile}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 truncate">{value}</p>
            </div>
          )}

          {/* Show URL input below upload for manual entry */}
          {!isUploaded && !isUploading && showUrlInput && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border" />
              <span>or paste a URL</span>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}
          {!isUploaded && !isUploading && showUrlInput && (
            <Input
              type="url"
              value={value.includes('/uploads/') ? '' : value}
              onChange={(e) => {
                onChange(e.target.value);
                setUploadedFile(null);
              }}
              placeholder={placeholder}
              className="text-sm"
            />
          )}
        </div>
      )}
    </div>
  );
}
